import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authBackend } from "./authBackend";
import { fetchMe } from "./api";
import { disablePush, refreshPush } from "./push";

const AuthContext = createContext(null);

async function loadMeWithRefresh() {
  try {
    return await fetchMe();
  } catch (err) {
    const status = err?.response?.status;
    // Token may be stale right after a hard refresh — refresh once and retry.
    if (status === 401) {
      const { data, error } = await authBackend.refreshSession();
      if (!error && data?.session) {
        return await fetchMe();
      }
      await authBackend.signOut();
    }
    throw err;
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  // Cached rows belong to whoever fetched them. Every cached query — groups,
  // workshops, stakeholders, terminology — is that account's brand's data, so
  // it must not survive into the next account's session.
  const lastAuthUserId = useRef(null);
  const [session, setSession] = useState(undefined); // undefined = not checked yet
  const [me, setMe] = useState(null); // { email, role, name, workshop_location, workshop_locations, service_advisor_id }
  const [meLoading, setMeLoading] = useState(false);

  const loadMe = useCallback(async (activeSession) => {
    if (!activeSession) {
      setMe(null);
      return;
    }
    setMeLoading(true);
    try {
      const data = await loadMeWithRefresh();
      setMe(data);
      // Signed in: keep this device's notification address fresh if the person already allowed it.
      refreshPush();
    } catch {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Drops every cached query when the signed-in identity changes, so one
    // brand's data is never shown to the next account to log in here.
    const clearIfIdentityChanged = (nextSession) => {
      const nextId = nextSession?.user?.id || null;
      if (lastAuthUserId.current !== nextId) {
        if (lastAuthUserId.current !== null) queryClient.clear();
        lastAuthUserId.current = nextId;
      }
    };

    // Restore from cookie storage before treating the app as logged-out.
    authBackend.getSession().then(({ data }) => {
      if (cancelled) return;
      clearIfIdentityChanged(data.session || null);
      setSession(data.session || null);
      loadMe(data.session || null);
    });

    const { data: sub } = authBackend.onAuthStateChange((event, newSession) => {
      if (cancelled) return;
      // Skip INITIAL_SESSION — getSession() above already handled the restore path
      // and avoids a double /auth/me race during refresh.
      if (event === "INITIAL_SESSION") return;
      clearIfIdentityChanged(newSession || null);
      setSession(newSession || null);
      loadMe(newSession || null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadMe, queryClient]);

  const signOut = async () => {
    // Before the session ends, so the server still knows who is asking to forget this device.
    await disablePush();
    await authBackend.signOut();
    setMe(null);
    // Belt and braces alongside the identity check above: never leave one
    // account's rows in the cache for the next person to sign in.
    queryClient.clear();
    lastAuthUserId.current = null;
  };

  const loading = session === undefined || (!!session && meLoading && me === null);

  return (
    <AuthContext.Provider
      value={{
        session: session || null,
        user: session?.user || null,
        me,
        role: me?.role || null,
        isMaster: me?.role === "master",
        loading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
